<template>
  <!-- Optimized image with fallback placeholder and error handling -->
  <div :class="['optimized-img-wrapper', imgClass]" :style="containerStyle">
    <picture v-if="usePicture && !imageError">
      <source :srcset="avifSrc" type="image/avif" v-if="avifSrc" />
      <source :srcset="webpSrc" type="image/webp" v-if="webpSrc" />
      <img
        :src="src"
        :alt="alt"
        :width="width"
        :height="height"
        :loading="loadingAttr"
        :fetchpriority="fetchPriorityAttr"
        class="img-element"
        @error="handleImageError"
        @load="$emit('load', $event)"
      />
    </picture>

    <img
      v-else-if="!imageError"
      :src="src"
      :alt="alt"
      :width="width"
      :height="height"
      :loading="loadingAttr"
      :fetchpriority="fetchPriorityAttr"
      class="img-element"
      @error="handleImageError"
      @load="$emit('load', $event)"
    />

    <!-- Fallback placeholder when image fails to load -->
    <div v-if="imageError" class="image-placeholder">
      <i class="fas fa-image placeholder-icon"></i>
      <span class="placeholder-text">{{ alt || 'Image' }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OptimizedImg',
  props: {
    src: { type: String, required: true },
    alt: { type: String, default: '' },
    width: { type: [Number, String], default: null },
    height: { type: [Number, String], default: null },
    eager: { type: Boolean, default: false },
    imgClass: { type: [String, Object, Array], default: '' },
    disablePicture: { type: Boolean, default: false }
  },
  data() {
    return {
      imageError: false
    }
  },
  computed: {
    // derive .avif/.webp variants by swapping extension if possible
    avifSrc() {
      if (this.disablePicture) return null
      return this.src.replace(/\.(jpe?g|png|webp|avif)(\?.*)?$/i, '.avif')
    },
    webpSrc() {
      if (this.disablePicture) return null
      return this.src.replace(/\.(jpe?g|png|webp|avif)(\?.*)?$/i, '.webp')
    },
    loadingAttr() {
      return this.eager ? 'eager' : 'lazy'
    },
    fetchPriorityAttr() {
      return this.eager ? 'high' : 'auto'
    },
    usePicture() {
      return !this.disablePicture && !!(this.avifSrc || this.webpSrc)
    },
    containerStyle() {
      // Make the image responsive: prefer width: 100% with an optional maxWidth when numeric sizes are supplied
      if (!this.width || !this.height) return { width: '100%' }
      return {
        width: '100%',
        maxWidth: typeof this.width === 'number' ? `${this.width}px` : this.width,
        aspectRatio: `${this.width} / ${this.height}`
      }
    }
  },
  methods: {
    handleImageError(event) {
      this.imageError = true
      this.$emit('error', event)
      console.warn(`Failed to load image: ${this.src}`)
    }
  }
}
</script>

<style scoped>
.optimized-img-wrapper {
  display: block;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.img-element {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: center;
}

/* Fallback placeholder styling (responsive small placeholder suitable for avatars) */
.image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%);
  color: #6b7280;
  font-size: 14px;
  padding: 0;
  text-align: center;
  box-sizing: border-box;
}


.placeholder-icon {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.placeholder-text {
  font-size: 12px;
  font-weight: 500;
}
</style>
